import React, { useEffect, useRef, useState } from 'react';
import { Redirect } from 'react-router-dom';
import { Box, Loader, TextButton } from '@wix/design-system';
import { ChevronLeft } from '@wix/wix-ui-icons-common/odeditor';
import { WIX_STUDIO_HEADER_HEIGHT } from '@wix/core-domain-search';
import { DomainSearchSuiteModule, EntriModule, mountEntriContainer, unmountEntriContainer, waitForEntriIframeReady } from '@wix/domains-search-suite';
import { ENTRI_ENABLED_BY_SPEC } from './connectEntriEligibility';
import { useQueryParams } from '../../hooks/useQueryParams';
export const ConnectEntriRoute = _ref => {
  let {
    onBack,
    handlers,
    externalViewer = false
  } = _ref;
  const {
    getQueryParam
  } = useQueryParams();
  const domainName = getQueryParam('domainName') ?? undefined;
  const metaSiteId = getQueryParam('metaSiteId') ?? undefined;
  const siteName = getQueryParam('siteName') ?? undefined;
  if (!domainName || !metaSiteId) {
    return /*#__PURE__*/React.createElement(Redirect, {
      to: "/"
    });
  }
  return /*#__PURE__*/React.createElement(DomainSearchSuiteModule.Provider, {
    translations: {
      Loading: /*#__PURE__*/React.createElement(React.Fragment, null)
    }
  }, /*#__PURE__*/React.createElement(ConnectEntriContent, {
    domainName: domainName,
    metaSiteId: metaSiteId,
    siteName: siteName,
    onBack: onBack,
    handlers: handlers,
    externalViewer: externalViewer
  }));
};
const ConnectEntriContent = _ref2 => {
  let {
    domainName,
    metaSiteId,
    siteName,
    onBack,
    handlers,
    externalViewer
  } = _ref2;
  const {
    t
  } = DomainSearchSuiteModule.internals.useTranslations();
  const hasStartedRef = useRef(false);
  const containerRef = useRef(null);
  const [isEntriIframeLoaded, setIsEntriIframeLoaded] = useState(false);
  const flowRef = useRef({
    domainName,
    metaSiteId,
    siteName,
    handlers,
    t
  });
  flowRef.current = {
    domainName,
    metaSiteId,
    siteName,
    handlers,
    t
  };
  const {
    stopConnectionModal
  } = EntriModule.internals.domainsEntriModalsController;
  const isExitingRef = useRef(false);
  const exitFlow = async function (_temp) {
    let {
      hideModal = false
    } = _temp === void 0 ? {} : _temp;
    if (isExitingRef.current) {
      return;
    }
    isExitingRef.current = true;
    await EntriModule.SDK.closeEntriFlow();
    if (hideModal) {
      stopConnectionModal.hide == null || stopConnectionModal.hide();
    }
    onBack == null || onBack({
      domainName,
      metaSiteId,
      siteName
    });
  };
  const showStopConnectionModal = function (isStopping) {
    if (isStopping === void 0) {
      isStopping = false;
    }
    stopConnectionModal.show({
      isStopping,
      onStop: () => {
        showStopConnectionModal(true);
        void exitFlow({
          hideModal: true
        });
      },
      onContinue: () => stopConnectionModal.hide == null ? void 0 : stopConnectionModal.hide()
    });
  };

  // Zero-arg wrapper: passing `showStopConnectionModal` straight to a handler
  // would feed it the event as `isStopping`.
  const handleBack = () => showStopConnectionModal();

  // Read through a ref so the Back-gesture guard below mounts once and never
  // re-pushes a history entry when the parent re-renders with a new onBack.
  const handleBackRef = useRef(handleBack);
  handleBackRef.current = handleBack;

  // Guard the phone/browser Back gesture: keep a sentinel entry on the stack so
  // Back lands back here and asks for confirmation, then closes Entri properly,
  // instead of the route being ripped out mid-flow.
  useEffect(() => {
    window.history.pushState({
      entriGuard: true
    }, '');
    const onPopState = () => {
      window.history.pushState({
        entriGuard: true
      }, '');
      handleBackRef.current();
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);
  useEffect(() => {
    if (hasStartedRef.current) {
      return;
    }
    hasStartedRef.current = true;
    if (containerRef.current) {
      mountEntriContainer({
        parent: containerRef.current,
        cssText: 'flex:1 1 auto; width: 100%;'
      });
    }
    let disposed = false;
    const entriReady = waitForEntriIframeReady();
    void entriReady.then(() => {
      if (!disposed) {
        setIsEntriIframeLoaded(true);
      }
    });
    const runEntri = async () => {
      var _flow$handlers;
      const flow = flowRef.current;
      const result = await EntriModule.SDK.startEntriFlow({
        domain: flow.domainName,
        siteName: flow.siteName ?? '',
        metaSiteId: flow.metaSiteId,
        skipIntro: true,
        fullPage: true,
        useCustomExitConfirmation: true,
        enabledBySpec: ENTRI_ENABLED_BY_SPEC,
        t: flow.t
      });
      await ((_flow$handlers = flow.handlers) == null || _flow$handlers.connect == null ? void 0 : _flow$handlers.connect({
        result: result.status,
        errorType: result.errorType,
        domainName: flow.domainName,
        msid: flow.metaSiteId,
        siteName: flow.siteName,
        externalViewer
      }));
    };
    void runEntri();
    return () => {
      disposed = true;
      void EntriModule.SDK.closeEntriFlow();
      unmountEntriContainer();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return /*#__PURE__*/React.createElement(Box, {
    direction: "vertical",
    width: "100%",
    height: `calc(100dvh - ${WIX_STUDIO_HEADER_HEIGHT})`
  }, /*#__PURE__*/React.createElement(Box, {
    padding: "SP3",
    flex: "0 0 auto"
  }, /*#__PURE__*/React.createElement(TextButton, {
    skin: "dark",
    prefixIcon: /*#__PURE__*/React.createElement(ChevronLeft, null),
    onClick: handleBack
  }, t('entri.intro_modal.back_cta'))), /*#__PURE__*/React.createElement(Box, {
    ref: containerRef,
    direction: "vertical",
    flex: "1 1 auto",
    align: "center",
    verticalAlign: "middle"
  }, !isEntriIframeLoaded && /*#__PURE__*/React.createElement(Loader, null)));
};
//# sourceMappingURL=ConnectEntriRoute.js.map